<template>
  <div>
    <div v-if="isMember" class="card-body">
      <div class="row mx-auto pb-3 justify-content-between">
        <div class="cols-3">
          <h3 class="mb-0">Downloads</h3>
        </div>
        <div class="cols-9" v-if="isMember && this.observationsTotal > 0">
          <button class="btn btn-primary" @click="onCreateDownload">
            <i class="fa fa-plus text-white"></i>
            New Download
          </button>
        </div>
      </div>
      <div class="row mx-auto pb-2 justify-content-end">
        <div class="cols-3">
          <ProjectNotificationStatus v-if="isMember" notification-type="download"></ProjectNotificationStatus>
        </div>
      </div>
      <div>
        <p>
          As per our Data Use Policy, all research publications using data from
          projects hosted on the CitSci platform (those listed on our Projects
          page) are required to cite the use of the platform. Citations provide
          CitSci credit as an integral component of methods and data collection.
          Recognition through citations is one metric funders use to evaluate
          our value and ensure that we can continue to provide data collection
          and support services to partners like you for free.
          Visit our
          <b-link :to="{ name: 'Cite' }">
            How to Cite CitSci
          </b-link>
          page for more details.
        </p>
        <p>
          If you do publish,
          <ContactUsIconLink hyperlink-text="let us know"></ContactUsIconLink>
          and we'll add it to our
          <b-link :to="{ name: 'Publications' }">Publications</b-link>
          page.
        </p>
      </div>
      <div class="row mx-auto justify-content-center">
        <h5 class="mb-2">
          <i class="fa-solid fa-circle-info fa-lg"></i>
          <b>Note:</b> <i>{{ getExpiryMessage() }}</i>
        </h5>
      </div>
      <b-table
        show-empty
        stacked="md"
        id="projectDownloadsTable"
        ref="projectDownloadsTable"
        :items="projectDownloads"
        :fields="visibleFields"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        :empty-text="emptyText"
        :sort-icon-left="true"
      >
        <template v-slot:cell(createdAt)="row">
          <DateTime :date="row.item.createdAt" :dateTime="false"></DateTime>
          <div class="small text-muted">{{ getExpiryDateString(row.item) }}</div>
        </template>
        <template v-slot:cell(citation)="row">
          <div v-if="row.item.status === 'completed' || row.item.status === 'expired'">
            <div>
              {{ row.item.project.name }}. ({{
                moment(row.item.createdAt).format("MMMM YYYY")
              }}). [Data Set]. <i>CitSci</i>
              <a :href=getProjectCitation(row.item.project)>
                {{ getProjectCitation(row.item.project) }}
              </a>
              Accessed: {{ getCitationAccessedDate(row.item) }}.
            </div>
          </div>
        </template>
        <template v-slot:cell(options)="row">
          <div class="options-holder right">
            <div class="btn btn-secondary btn-sm"
                 v-if="row.item.status === 'completed' && row.item.fileObject !== null"
                 @click="onClickViewDownload(row.item.fileObject)"
                 title="Download File"
            >
              <i class="fa fa-download mr-2"></i>Download
            </div>
            <div class="btn btn-secondary btn-sm mt-1 mr-1 my-1"
                 v-if="row.item.status !== 'completed' && row.item.status !== 'failed' && row.item.status !== 'expired'"
                 @click="onClickRefresh()"
                 title="Refresh Status"
            >
              <i class="fa fa-refresh mr-2"></i> Refresh
            </div>
          </div>
        </template>
        <template #table-busy>
          <VueLoader
            style="height: 200px"
            text="Loading Project Downloads..."
          />
        </template>
      </b-table>
    </div>
    <RequiresMember v-else page="Downloads"></RequiresMember>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import VueLoader from "../../../layout/Loader";
import RequiresMember from "../tabpermission/requiresMember.vue";
import DateTime from "../../../../components/common/date-time/DateTime.vue";
import moment from "moment";
import IconLink from "../../../layout/IconLink.vue";
import ContactUsIconLink from "../../../layout/ContactUsIconLink.vue";
import ProjectNotificationStatus from "../notifications/ProjectNotificationStatus.vue";

export default {
  name: "ProjectDownload",
  components: {
    ContactUsIconLink,
    IconLink,
    ProjectNotificationStatus,
    RequiresMember,
    VueLoader,
    DateTime,
  },

  data() {
    return {
      isBusy: false,
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      sortBy: "createdAt",
      sortDesc: true,
      sortDirection: "desc",
      emptyText: "No Downloads Yet!",

      fieldList: [
        {
          key: "createdAt",
          label: "Created Date",
          sortable: true,
          class: "text-left",
          visible: true,
        },
        {
          key: "status",
          label: "Status",
          sortable: false,
          class: "text-left",
          visible: true,
          formatter: (val) => {
            return val.charAt(0).toUpperCase() + val.slice(1);
          },
        },
        {
          key: "citation",
          label: "Citation",
          sortable: false,
          class: "text-left",
          visible: true,
        },
        {
          key: "options",
          label: "Options",
          class: "text-right",
          visible: true,
        },
      ],
    };
  },

  computed: {
    ...mapGetters({
      userIri: "user/profile/iri",
      projectId: "project/get/id",
      project: "project/get/project",
      isMember: "project/get/isMember",
      lastNotification: "user/notifications/lastNotification",
    }),
    ...mapState({
      observationsTotal: (state) => state.project.get.stats.observationsTotal,
    }),
    visibleFields() {
      // Property returns only the fields where visible = true
      return this.fieldList.filter((field) => field.visible);
    },
  },

  watch: {
    lastNotification(newVal) {
      if (
        newVal &&
        newVal.type === 'project_download' &&
        newVal.projectUrl === this.project.urlField && this.$refs.projectDownloadsTable
      ) {
        this.$refs.projectDownloadsTable.refresh();
      }
    },
  },

  methods: {
    ...mapActions({
      createProjectDownload: "project/project_downloads/create",
      getProjectDownloads: "project/project_downloads/get",
    }),
    moment(value) {
      return moment(value);
    },
    onCreateDownload() {
      let data = {
        user: this.userIri,
        project: "/projects/" + this.projectId,
      };
      this.createProjectDownload(data).then((data) => {
        this.onClickRefresh();
      });
      // setTimeout(()=>{ this.onClickRefresh() },5000);
    },

    onClickRefresh() {
      this.$refs.projectDownloadsTable.refresh();
    },

    onClickViewDownload(data) {
      if (data.path !== null || data.path !== "") {
        window.open(data.path, "_blank");
      }
    },

    getExpiryMessage() {
      let start = new Date('2026-11-01')
      if (new Date() < start) {
        return 'Starting November 1st, 2026 data downloads older than 30 days will be automatically removed. Your request history is kept, and you can generate a new download at any time.'
      }
      return 'Data downloads older than 30 days are automatically removed. Your request history is kept, and you can generate a new download at any time.'
    },

    getExpiryDateString(download) {
      let status = download.status
      if (status === 'completed') {
        let expires = this.moment(download.createdAt).add(30, 'days')
        let start = this.moment('2026-11-01')
        // Removal does not begin until the start date, so anything due before then lasts at least that long.
        if (expires.isBefore(start)) {
          return "Expires: " + start.format('MMM DD, YYYY')
        }
        return "Expires: " + expires.format('MMM DD, YYYY')
      }
      else if (status === 'expired') {
          return "Expired: " + this.moment(download.updatedAt).format('MMM DD, YYYY')
      }
      else {
        return null
      }
    },

    getCitationAccessedDate(download) {
      // An expired file can no longer be accessed, so the citation uses the last full day it was available.
      let accessed = download.status === 'expired'
        ? this.moment(download.updatedAt).subtract(1, 'days')
        : this.moment(Date.now())
      return accessed.format('MMMM DD, YYYY')
    },

    getProjectCitation(project) {
      return this.$appRootUrl + '/projects/' + project.urlField
    },

    projectDownloads(ctx) {
      this.isBusy = true;
      let urlParams = `${this.urlBuilder(ctx)}`;
      const data = {
        user: this.userIri,
        projectId: this.projectId,
        urlParams,
      };

      return this.getProjectDownloads(data).then((data) => {
        this.totalItems = data["hydra:totalItems"];
        this.currentPage = ctx.currentPage || 1;
        this.sortBy = ctx.sortBy || "";
        this.isBusy = false;
        return data["hydra:member"].map((downloads) => {
          return {
            ...downloads,
          };
        })
        // Uncomment to color rows based on status column
        // .map(item => {
        //   item._rowVariant = this.getRowColor(item.status)
        //   return item
        // })
      });
    },

    getRowColor(status) {
      // Returns row color
      switch (status) {
        case "completed":
          return "success";
        case "submitted":
        case "in progress":
          return "active";
        case "failed":
        case "expired":
          return "danger";
      }
    },
  },
};
</script>

<style></style>
